import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Popover, Segmented, Select, Switch, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ApartmentOutlined, FileExcelOutlined, FilterOutlined, InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getStatusTagColor } from '../../../utils/orderStatus';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import { useFeedback } from '../../../utils/feedback';
import { MARGIN_BUCKET_THRESHOLDS } from '../profitabilityAnalysis/analysis';
import { CostPartialMark, ReportLongTable, ReportSection } from '../ReportTab';
import type { OrderInResultRow } from './ordersInResult';
import {
  buildOrderPivot,
  NO_PIVOT_FILTERS,
  measureChange,
  measureIncomplete,
  measureValue,
  pivotFilterOptions,
  pivotStatusOptions,
  sortPivot,
  type PivotCells,
  type PivotColumns,
  type PivotDimension,
  type PivotMeasure,
  type PivotMoney,
  type PivotNode,
  type PivotOrderInfo,
  type PivotScope,
} from './orderPivot';
import { activePivotFilterCount, readPivotView, writePivotView, type PivotViewState } from './pivotViewState';
import { buildPivotExportTable } from './pivotExport';
import { exportSalesPivot } from './incomeStatementApi';
import PivotTrendChart from './PivotTrendChart';

interface Props {
  rows: OrderInResultRow[];
  orderInfo: Map<string, PivotOrderInfo>;
  /** The report period as the page names it („Cały okres (2025–2026)”), for the export. */
  periodLabel: string;
  /** Why the order is in the actual, the forecast or both (its whole period), for the leaf tooltip. */
  whyOf?: (row: OrderInResultRow) => string[];
}

interface TreeRow {
  key: string;
  label: React.ReactNode;
  /** Plain name for the trend chart title. */
  name: string;
  cells: PivotCells;
  orderCount: number | null;
  status?: string;
  children?: TreeRow[];
}

const DIMENSIONS: PivotDimension[] = ['manager', 'customer', 'product'];
const COLUMN_CHOICES: PivotColumns[] = ['total', 'month', 'quarter', 'year'];
const MEASURES: PivotMeasure[] = ['revenue', 'cost', 'margin', 'marginPercent', 'orders'];
const SCOPES: PivotScope[] = ['all', 'actual', 'forecast'];
const ROMAN = ['I', 'II', 'III', 'IV'];
const MONTHS_PL = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

function marginClass(margin: number | null, revenue: number): string {
  if (margin == null || !(revenue > 0)) return '';
  const percent = (margin / revenue) * 100;
  if (percent < 0) return 'is-margin-negative';
  if (percent < MARGIN_BUCKET_THRESHOLDS.low) return 'is-margin-low';
  if (percent < MARGIN_BUCKET_THRESHOLDS.medium) return '';
  return 'is-margin-good';
}

// „Przekrój sprzedaży”: who sells what to whom — account manager, customer or
// product, optionally a second level, the orders as leaves; in one sum or per
// month / quarter / year with the trend of the chosen row. Every setting is in
// the address (pivotViewState.ts), so „Kopiuj link” shares this exact cut.
const OrderPivotSection: React.FC<Props> = ({ rows, orderInfo, periodLabel, whyOf }) => {
  const { t, i18n } = useTranslation(['reports', 'orders']);
  const feedback = useFeedback();
  const language = i18n.language || 'pl';
  const k = (key: string, options?: Record<string, unknown>) => t(`reports:profitability.orderPivot.${key}`, options ?? {}) as string;
  const [searchParams, setSearchParams] = useSearchParams();
  const view = readPivotView(searchParams);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const update = (patch: Partial<PivotViewState>) => {
    // A bucket sort means nothing in other columns: back to the default order.
    const sort = patch.columns && patch.columns !== view.columns ? null : view.sort;
    setSearchParams(writePivotView(searchParams, { ...view, sort, ...patch }), { replace: true });
  };

  const viewKey = searchParams.toString();
  const result = useMemo(
    () => sortPivot(buildOrderPivot(rows, orderInfo, {
      first: view.first, second: view.second, columns: view.columns, scope: view.scope, filters: view.filters, unknownProduct: k('noProduct'),
    }), view.sort, view.measure),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, orderInfo, viewKey],
  );
  const filterOptions = useMemo(
    () => pivotFilterOptions(rows, orderInfo, k('noProduct')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, orderInfo],
  );
  const statusOptions = useMemo(() => pivotStatusOptions(rows), [rows]);
  const statusLabel = (status: string) => t(`orders:statusOptions.${status}`, { defaultValue: status }) as string;
  const byPeriod = view.columns !== 'total' && result.buckets.length > 0;

  const bucketLabel = (bucket: string): string => {
    if (/^\d{4}$/.test(bucket)) return bucket;
    if (bucket.includes('-Q')) return k('quarter', { roman: ROMAN[Number(bucket.slice(6)) - 1], year: bucket.slice(2, 4) });
    return `${MONTHS_PL[Number(bucket.slice(5, 7)) - 1]} ${bucket.slice(2, 4)}`;
  };

  // Whole złoty: a product share of an order is an estimate, grosze would be noise.
  const zl = (value: number) => `${formatLocalizedNumber(Math.round(value), 0, language)} zł`;
  const formatMeasure = (value: number | null, measure: PivotMeasure = view.measure): string => {
    if (value == null) return '—';
    if (measure === 'marginPercent') return `${formatLocalizedNumber(value, 1, language)}%`;
    if (measure === 'orders') return formatLocalizedNumber(value, 0, language);
    return zl(value);
  };
  // Period cells are narrow: „64,6 tys.”, the exact amount in the tooltip and the Excel file.
  const formatShort = (value: number | null): string => {
    if (value == null || view.measure === 'marginPercent' || view.measure === 'orders') return formatMeasure(value);
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${formatLocalizedNumber(value / 1_000_000, 2, language)} mln`;
    if (abs >= 1_000) return `${formatLocalizedNumber(value / 1_000, abs >= 100_000 ? 0 : 1, language)} tys.`;
    return zl(value);
  };
  const formatChange = (value: number | null): string | null => {
    if (value == null) return null;
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    const amount = formatLocalizedNumber(Math.abs(value), view.measure === 'marginPercent' ? 1 : 0, language);
    return view.measure === 'marginPercent' ? k('changePoints', { value: `${sign}${amount}` }) : `${sign}${amount}%`;
  };

  // ── Tree rows ───────────────────────────────────────────────────────────
  const statusTag = (status: string) => (
    <Tag className="order-pivot__status" color={getStatusTagColor(status)}>
      {statusLabel(status)}
    </Tag>
  );
  const showCustomer = view.first !== 'customer' && view.second !== 'customer';
  const toTree = (node: PivotNode, path: string): TreeRow => {
    const key = `${path}/${node.key}`;
    const leaves: TreeRow[] = node.orders.map((leaf) => ({
      key: `${key}/${leaf.row.id}`,
      name: leaf.row.displayOrderNumber,
      label: (
        <span className="order-pivot__order">
          <Link to={`/app/orders/${leaf.row.id}`} onClick={(event) => event.stopPropagation()}>{leaf.row.displayOrderNumber}</Link>
          {showCustomer && <span className="report-muted report-cut" title={leaf.row.customerName}>{leaf.row.customerName}</span>}
          {leaf.share < 0.9995 && <span className="report-muted">{k('share', { percent: Math.round(leaf.share * 100) })}</span>}
          {leaf.row.status && statusTag(leaf.row.status)}
          {whyOf && (
            <Tooltip title={<span className="order-pivot__why">{whyOf(leaf.row).map((line) => <span key={line}>{line}</span>)}</span>}>
              <InfoCircleOutlined className="order-pivot__why-icon" aria-label={k('why')} onClick={(event) => event.stopPropagation()} />
            </Tooltip>
          )}
        </span>
      ),
      cells: leaf,
      orderCount: null,
      status: leaf.row.status,
    }));
    return {
      key,
      name: node.label,
      label: <span className="report-cut" title={node.label}>{node.label}</span>,
      cells: node,
      orderCount: node.orderIds.size,
      children: node.children.length ? node.children.map((child) => toTree(child, key)) : leaves,
    };
  };
  const data = result.nodes.map((node) => toTree(node, 'root'));
  const findRow = (list: TreeRow[], key: string): TreeRow | null => {
    for (const row of list) {
      if (row.key === key) return row;
      const found = row.children ? findRow(row.children, key) : null;
      if (found) return found;
    }
    return null;
  };
  const selected = selectedKey ? findRow(data, selectedKey) : null;

  // ── Columns ─────────────────────────────────────────────────────────────
  const dimensionTitle = k(`dimension.${view.first}`) + (view.second ? ` → ${k(`dimension.${view.second}`)}` : '');
  // Sorting happens in the model (sortPivot), so the export follows it; the
  // table only shows the arrows and reports a click.
  const sortable = (key: string) => ({
    sorter: true,
    sortDirections: ['descend', 'ascend'] as ('descend' | 'ascend')[],
    sortOrder: view.sort?.key === key ? (view.sort.direction === 'asc' ? 'ascend' as const : 'descend' as const) : null,
  });
  const labelColumn: ColumnsType<TreeRow>[number] = {
    title: dimensionTitle,
    key: 'label',
    ...sortable('label'),
    width: byPeriod ? 260 : undefined,
    fixed: byPeriod ? 'left' : undefined,
    ellipsis: true,
    render: (_, row) => row.label,
  };
  const dash = <span className="report-muted">—</span>;
  // Margin % of the amounts shown; marked as overstated with an incomplete cost.
  const percentCell = (money: PivotMoney, className = '') => {
    const value = measureValue(money, 'marginPercent');
    const partial = money.pendingOrderIds.size > 0;
    const hint = t('reports:profitability.percentPartial', { count: money.pendingOrderIds.size });
    if (value == null) return <span className="report-muted" title={partial ? hint : undefined}>—</span>;
    return (
      <span className={`report-num ${className}`} title={partial ? hint : undefined}>
        {formatMeasure(value, 'marginPercent')}
        {partial && <CostPartialMark hint={hint} testId="pivot-percent-partial" />}
      </span>
    );
  };

  const periodCell = (money: PivotMoney | undefined, previous: PivotMoney | undefined) => {
    const value = measureValue(money, view.measure);
    if (value == null) return dash;
    const change = view.change ? formatChange(measureChange(money, previous, view.measure)) : null;
    const changeValue = view.change ? measureChange(money, previous, view.measure) : null;
    return (
      <span className="order-pivot__cell">
        <span className={`report-num${value < 0 ? ' report-negative' : ''}`} title={formatMeasure(value)}>
          {formatShort(value)}{(measureIncomplete(money, view.measure)) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}
        </span>
        {view.change && (
          <span className={`order-pivot__change${changeValue != null && changeValue < 0 ? ' report-negative' : changeValue ? ' report-positive' : ''}`}>
            {change ?? '·'}
          </span>
        )}
      </span>
    );
  };

  const bucketKind = (bucket: string): 'settled' | 'mixed' | 'forecast' => {
    const money = result.byBucket.get(bucket);
    if (!money || money.waiting <= 0) return 'settled';
    return money.invoiced > 0 ? 'mixed' : 'forecast';
  };

  const columns: ColumnsType<TreeRow> = byPeriod
    ? [
      labelColumn,
      ...result.buckets.map((bucket, index) => {
        const kind = bucketKind(bucket);
        return {
          title: (
            <span className="order-pivot__bucket" title={kind === 'settled' ? undefined : k(`bucketHint.${kind}`)}>
              {bucketLabel(bucket)}
              {kind !== 'settled' && <span className={`order-pivot__bucket-mark is-${kind}`} />}
            </span>
          ),
          key: bucket,
          ...sortable(bucket),
          width: view.measure === 'orders' ? 72 : 96,
          align: 'right' as const,
          className: kind === 'forecast' ? 'order-pivot__col-forecast' : undefined,
          render: (_: unknown, row: TreeRow) => periodCell(row.cells.byBucket.get(bucket), index > 0 ? row.cells.byBucket.get(result.buckets[index - 1]) : undefined),
        };
      }),
      {
        title: k('total'),
        key: 'total',
        ...sortable('total'),
        width: 132,
        align: 'right' as const,
        fixed: 'right' as const,
        className: 'order-pivot__col-total',
        render: (_: unknown, row: TreeRow) => {
          const value = measureValue(row.cells.total, view.measure);
          return value == null ? dash : (
            <span className={`report-num${value < 0 ? ' report-negative' : ''}`}>
              {formatMeasure(value)}{(measureIncomplete(row.cells.total, view.measure)) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}
            </span>
          );
        },
      },
    ]
    : [
      labelColumn,
      { title: k('colOrders'), key: 'orders', ...sortable('orders'), width: 72, align: 'right', render: (_, row) => (row.orderCount == null ? '' : <span className="report-num">{row.orderCount}</span>) },
      { title: k('colInvoiced'), key: 'invoiced', ...sortable('invoiced'), width: 150, align: 'right', render: (_, row) => (row.cells.total.invoiced ? <span className="report-num">{zl(row.cells.total.invoiced)}</span> : dash) },
      { title: k('colWaiting'), key: 'waiting', ...sortable('waiting'), width: 160, align: 'right', render: (_, row) => (row.cells.total.waiting ? <span className="report-num">{zl(row.cells.total.waiting)}</span> : dash) },
      {
        title: k('colCost'),
        key: 'cost',
        ...sortable('cost'),
        width: 128,
        align: 'right',
        render: (_, row) => (
          <span className="report-num">{zl(row.cells.total.cost)}{(row.cells.total.marginIncomplete) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}</span>
        ),
      },
      {
        title: k('colMargin'),
        key: 'margin',
        ...sortable('margin'),
        width: 128,
        align: 'right',
        render: (_, row) => {
          const money = row.cells.total;
          return (
            <span className={`report-num order-pivot__margin ${money.marginIncomplete ? '' : marginClass(money.margin, money.invoiced + money.waiting)}`}>
              {zl(money.margin)}{(money.marginIncomplete) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}
            </span>
          );
        },
      },
      {
        title: k('colMarginPercent'),
        key: 'marginPercent',
        ...sortable('marginPercent'),
        width: 104,
        align: 'right',
        render: (_, row) => {
          const money = row.cells.total;
          // A margin of a known part of the cost is not judged by colour.
          return percentCell(money, `order-pivot__margin ${money.marginIncomplete ? '' : marginClass(money.margin, money.invoiced + money.waiting)}`);
        },
      },
    ];

  const summaryCells = byPeriod
    ? [
      ...result.buckets.map((bucket, index) => periodCell(result.byBucket.get(bucket), index > 0 ? result.byBucket.get(result.buckets[index - 1]) : undefined)),
      <span className="report-num">{formatMeasure(measureValue(result.total, view.measure))}{(measureIncomplete(result.total, view.measure)) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}</span>,
    ]
    : [
      <span className="report-num">{result.total.orderIds.size}</span>,
      <span className="report-num">{zl(result.total.invoiced)}</span>,
      <span className="report-num">{zl(result.total.waiting)}</span>,
      <span className="report-num">{zl(result.total.cost)}{(result.total.marginIncomplete) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}</span>,
      <span className="report-num">{zl(result.total.margin)}{(result.total.marginIncomplete) && <CostPartialMark hint={k('incompleteHint')} testId="pivot-partial" />}</span>,
      percentCell(result.total),
    ];

  // ── Controls, filters, link, export ─────────────────────────────────────
  const option = <T extends string>(prefix: string) => (value: T) => ({ value, label: k(`${prefix}.${value}`) });
  // Rows and their second level in one list: „Handlowiec → Klient”.
  const rowChoices = DIMENSIONS.flatMap((first) => [null, ...DIMENSIONS.filter((value) => value !== first)].map((second) => ({
    value: `${first}>${second ?? 'none'}`,
    label: k(`dimension.${first}`) + (second ? ` → ${k(`dimension.${second}`)}` : ''),
  })));
  // The scope counts as a filter: it narrows the orders like the others.
  const filterCount = activePivotFilterCount(view.filters) + (view.scope === 'all' ? 0 : 1);
  const filterSelect = (dimension: PivotDimension) => (
    <label className="order-pivot__filter">
      <span className="report-muted">{k(`dimension.${dimension}`)}</span>
      <Select
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        value={view.filters[dimension]}
        onChange={(value: string[]) => update({ filters: { ...view.filters, [dimension]: value } })}
        placeholder={k(`filterAll.${dimension}`)}
        options={filterOptions[dimension].map((option) => ({ value: option.key, label: option.label }))}
        optionFilterProp="label"
        aria-label={k(`dimension.${dimension}`)}
        // Render the list inside the popover: a portal counts as an outside click and closes it.
        getPopupContainer={(node) => node.parentElement ?? document.body}
      />
    </label>
  );
  const filtersPanel = (
    <div className="order-pivot__filters" role="group" aria-label={k('filters')}>
      <label className="order-pivot__filter">
        <span className="report-muted">{k('scopeLabel')}</span>
        <Segmented<PivotScope>
          block
          value={view.scope}
          onChange={(value) => update({ scope: value })}
          options={SCOPES.map(option<PivotScope>('scope'))}
        />
      </label>
      {DIMENSIONS.map((dimension) => <React.Fragment key={dimension}>{filterSelect(dimension)}</React.Fragment>)}
      <label className="order-pivot__filter">
        <span className="report-muted">{k('status')}</span>
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          value={view.filters.status}
          onChange={(value: string[]) => update({ filters: { ...view.filters, status: value } })}
          placeholder={k('filterAll.status')}
          options={statusOptions.map((status) => ({ value: status, label: statusLabel(status) }))}
          optionFilterProp="label"
          aria-label={k('status')}
          getPopupContainer={(node) => node.parentElement ?? document.body}
        />
      </label>
      <label className="order-pivot__toggle" title={k('hideNoCostHint')}>
        <Switch
          size="small"
          checked={view.filters.hideNoCost}
          onChange={(checked) => update({ filters: { ...view.filters, hideNoCost: checked } })}
          aria-label={k('hideNoCost')}
        />
        <span>{k('hideNoCost')}</span>
      </label>
      {filterCount > 0 && (
        <Button type="link" size="small" onClick={() => update({ scope: 'all', filters: NO_PIVOT_FILTERS })}>
          {k('clearFilters')}
        </Button>
      )}
    </div>
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      feedback.success(k('linkCopied'));
    } catch {
      feedback.error(k('linkCopyFailed'));
    }
  };

  const filterNames = (dimension: PivotDimension) => view.filters[dimension]
    .map((key) => filterOptions[dimension].find((option) => option.key === key)?.label || key);
  const exportInfo = (): string[] => {
    const filters = DIMENSIONS.filter((dimension) => view.filters[dimension].length)
      .map((dimension) => `${k(`dimension.${dimension}`)}: ${filterNames(dimension).join(', ')}`);
    if (view.filters.status.length) filters.push(`${k('status')}: ${view.filters.status.map(statusLabel).join(', ')}`);
    if (view.filters.hideNoCost) filters.push(k('hideNoCost'));
    return [
      k('info.period', { period: periodLabel }),
      k('info.rows', { rows: dimensionTitle }),
      byPeriod
        ? k('info.columns', { columns: k(`columns.${view.columns}`), measure: k(`measure.${view.measure}`) })
        : k('info.columnsTotal'),
      k('info.scope', { scope: k(`scope.${view.scope}`) }),
      filters.length ? k('info.filters', { filters: filters.join(' · ') }) : k('info.noFilters'),
      k('info.marginPercentRule'),
      ...(result.total.marginIncomplete ? [k('incompleteHint')] : []),
      k('info.link', { url: window.location.href }),
    ];
  };
  const runExport = async () => {
    setExporting(true);
    try {
      const table = buildPivotExportTable(result, view, {
        title: k('title'),
        info: exportInfo(),
        labelHeader: dimensionTitle,
        bucket: bucketLabel,
        total: k('total'),
        orders: k('colOrders'),
        invoiced: k('colInvoiced'),
        waiting: k('colWaiting'),
        cost: k('colCost'),
        margin: k('colMargin'),
        marginPercent: k('colMarginPercent'),
        orderLabel: (leaf) => [
          leaf.row.displayOrderNumber,
          showCustomer ? leaf.row.customerName : null,
          leaf.share < 0.9995 ? k('share', { percent: Math.round(leaf.share * 100) }) : null,
        ].filter(Boolean).join(' · '),
      });
      const file = await exportSalesPivot(table);
      const url = window.URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(k('exported', { filename: file.filename }));
    } catch {
      feedback.error(k('exportFailed'));
    } finally {
      setExporting(false);
    }
  };


  return (
    <ReportSection
      title={k('title')}
      icon={<ApartmentOutlined />}
      tone="indigo"
      count={result.total.orderIds.size}
      lead={k('lead')}
      className="order-pivot-section"
      actions={(
        <>
          <Button size="small" icon={<LinkOutlined />} onClick={copyLink}>{k('copyLink')}</Button>
          <Button size="small" icon={<FileExcelOutlined />} loading={exporting} disabled={!result.nodes.length} onClick={runExport}>{k('export')}</Button>
        </>
      )}
    >
      {/* One row like Analiza: filled selects with their name inside, scope in Filtry. */}
      <div className="report-controls order-pivot__controls">
        <Select<string>
          className="order-pivot__select is-rows"
          prefix={<span className="report-muted">{k('rows')}</span>}
          value={`${view.first}>${view.second ?? 'none'}`}
          onChange={(value) => {
            const [first, second] = value.split('>') as [PivotDimension, PivotDimension | 'none'];
            update({ first, second: second === 'none' ? null : second });
          }}
          options={rowChoices}
          popupMatchSelectWidth={false}
          aria-label={k('rows')}
        />
        <Select<PivotColumns>
          className="order-pivot__select"
          prefix={<span className="report-muted">{k('columnsLabel')}</span>}
          value={view.columns}
          onChange={(value) => update({ columns: value })}
          options={COLUMN_CHOICES.map(option<PivotColumns>('columns'))}
          popupMatchSelectWidth={false}
          aria-label={k('columnsLabel')}
        />
        {view.columns !== 'total' && (
          <Select<PivotMeasure>
            className="order-pivot__select"
            prefix={<span className="report-muted">{k('measureLabel')}</span>}
            value={view.measure}
            onChange={(value) => update({ measure: value })}
            options={MEASURES.map(option<PivotMeasure>('measure'))}
            popupMatchSelectWidth={false}
            aria-label={k('measureLabel')}
          />
        )}
        <Popover trigger="click" placement="bottomLeft" content={filtersPanel}>
          <Badge count={filterCount} size="small">
            <Button icon={<FilterOutlined />}>{k('filters')}</Button>
          </Badge>
        </Popover>
        {view.columns !== 'total' && (
          <div className="report-controls__end">
            <label className="order-pivot__toggle" title={k('changeHint')}>
              <Switch size="small" checked={view.change} onChange={(checked) => update({ change: checked })} aria-label={k('change')} />
              <span>{k('change')}</span>
            </label>
          </div>
        )}
      </div>

      {byPeriod && (
        <div className="order-pivot__trend">
          <div className="order-pivot__trend-head">
            <span className="order-pivot__trend-title report-cut" title={selected?.name}>
              {selected ? k('chartTitle', { label: selected.name }) : k('chartAll')}
            </span>
            {selected && <Button type="link" size="small" onClick={() => setSelectedKey(null)}>{k('chartShowAll')}</Button>}
            <span className="order-pivot__legend">
              <span className="order-pivot__legend-item"><span className="order-pivot__swatch is-settled" />{k('legendActual')}</span>
              <span className="order-pivot__legend-item"><span className="order-pivot__swatch is-forecast" />{k('legendForecast')}</span>
            </span>
          </div>
          <PivotTrendChart
            buckets={result.buckets}
            cells={selected?.cells ?? result}
            measure={view.measure}
            bucketLabel={bucketLabel}
            formatValue={(value) => formatMeasure(value)}
            language={language}
          />
        </div>
      )}

      <ReportLongTable rowCount={data.length} testId="order-pivot-rows">
        <div className={`report-table order-pivot${byPeriod ? ' is-by-period' : ''}`}>
          <Table<TreeRow>
            rowKey="key"
            size="small"
            tableLayout="fixed"
            columns={columns}
            dataSource={data}
            pagination={false}
            scroll={byPeriod ? { x: 'max-content' } : undefined}
            locale={{ emptyText: k('empty') }}
            showSorterTooltip={false}
            onChange={(_pagination, _filters, sorter) => {
              const one = Array.isArray(sorter) ? sorter[0] : sorter;
              const key = one?.order ? String(one.columnKey) : null;
              update({ sort: key ? { key, direction: one.order === 'ascend' ? 'asc' : 'desc' } : null });
            }}
            rowClassName={(row) => (row.key === selectedKey ? 'order-pivot__row is-selected' : 'order-pivot__row')}
            onRow={(row) => ({ onClick: () => setSelectedKey(row.key === selectedKey ? null : row.key) })}
            expandable={{ indentSize: 20 }}
            summary={() => (data.length ? (
              <Table.Summary fixed={byPeriod ? 'bottom' : undefined}>
                <Table.Summary.Row className="order-pivot__total">
                  <Table.Summary.Cell index={0}>{k('total')}</Table.Summary.Cell>
                  {summaryCells.map((cell, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Table.Summary.Cell key={index} index={index + 1} align="right">{cell}</Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            ) : null)}
          />
        </div>
      </ReportLongTable>
      {result.total.marginIncomplete && <p className="report-muted order-pivot__note report-cut" title={k('incompleteHint')}>{k('incomplete')}</p>}
    </ReportSection>
  );
};

export default OrderPivotSection;
